// Polish Phase 2 — share ONE ParticleSystem across the Pixi scene.
//
// LetterCubeSprite emits cube-lifecycle bursts (rune-carve / stone-fall /
// stone-carve) and EffectLayer renders + drives them — but they are sibling
// layers in World. A React context (which works inside the @pixi/react tree —
// useTheme() already proves it) hands both the same instance. The provider
// owns the system's lifecycle: created once, disposed on match unmount.

import {
  createContext,
  type JSX,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { ParticleSystem } from './particle-system'

const ParticleSystemContext = createContext<ParticleSystem | null>(null)

export function ParticleSystemProvider({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  // PERF-LEAK-01: under React StrictMode (dev), the provider mounts →
  // unmounts → remounts. A naive useRef + dispose-on-cleanup left the system
  // disposed-but-never-recreated, so every cube burst rendered nothing in dev.
  //
  // Robust pattern: hold the system in state so a recreation re-renders and
  // propagates the LIVE instance to consumers (LetterLayer/EffectLayer re-read
  // it). One effect revives a StrictMode-disposed instance; a SEPARATE
  // empty-dep effect disposes only on a true unmount (via a ref to the current
  // instance), avoiding the dispose→recreate loop a dependency-tracked cleanup
  // would cause.
  const [system, setSystem] = useState<ParticleSystem>(
    () => new ParticleSystem(),
  )
  const systemRef = useRef(system)
  systemRef.current = system

  useEffect(() => {
    if (system.isDisposed) setSystem(new ParticleSystem())
  }, [system])

  useEffect(() => {
    return () => {
      systemRef.current.dispose()
    }
  }, [])

  return (
    <ParticleSystemContext.Provider value={system}>
      {children}
    </ParticleSystemContext.Provider>
  )
}

/** The shared match ParticleSystem, or null outside a provider. */
export function useParticleSystem(): ParticleSystem | null {
  return useContext(ParticleSystemContext)
}
